// components/expenses/Expenses.jsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Stack,
  TextField, CircularProgress, ToggleButtonGroup, ToggleButton
} from '@mui/material';
import {
  Add, FilterList, Clear as ClearIcon,
  Receipt as ReceiptIcon, Category as CategoryIcon,
  FileDownload as FileDownloadIcon,
  DeleteSweep as TrashIcon,
  ListAlt as ActiveIcon
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { userAPI, expenseAPI, branchAPI, expenseCategoryAPI } from '../../services/api';
import {
  searchExpenseCategories,
  searchUsers,
  searchBranches,
} from '../../services/optimizedSearchService';
import { useAuth } from '../../context/AuthContext';
import NoPrivilege from '../../components/common/NoPrivilege';
import NoContent from '../../components/common/NoContent';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ExpensesTable from './ExpensesTable';
import DeletedExpensesTable from './DeletedExpensesTable';
import AddExpenseDialog from './AddExpenseDialog';
import DeleteExpenseDialog from './DeleteExpenseDialog';
import ManageCategoriesDialog from './ManageCategoriesDialog';
import MultiSelectSearchAutocomplete from '../../components/common/MultiSelectSearchAutocomplete';

// ─── Status options (static, no API needed) ──────────────────────────────────
const STATUS_VALUES = ['pending', 'approved', 'rejected', 'paid'];

const Expenses = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // ── View Mode: 'active' or 'deleted' ─────────────────────────────────
  const [viewMode, setViewMode] = useState('active');

  // ── Data ──────────────────────────────────────────────────────────────
  const [expenses, setExpenses] = useState([]);
  const [deletedExpenses, setDeletedExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [privilegeError, setPrivilegeError] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalDeletedExpenses, setTotalDeletedExpenses] = useState(0);
  const [deletedTotalAmount, setDeletedTotalAmount] = useState(0);
  const [exporting, setExporting] = useState(false);

  // ── Pagination ────────────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deletedPage, setDeletedPage] = useState(0);
  const [deletedRowsPerPage, setDeletedRowsPerPage] = useState(10);

  // ── Dialogs ───────────────────────────────────────────────────────────
  const [openExpenseDialog, setOpenExpenseDialog] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState({
    title: '', message: '', onConfirm: null
  });

  // ── Filters — all multi-select now ────────────────────────────────────
  const [filters, setFilters] = useState({
    statuses: [],
    categoryIds: [],
    createdByIds: [],
    branchIds: [],
    from: null,
    to: null,
    search: '',
  });

  // ── Permissions ───────────────────────────────────────────────────────
  const canView = user?.isSuperAdmin || user?.privileges?.some(p => p.module === 'expenses' && p.operations.includes('view'));
  const canAdd = user?.isSuperAdmin || user?.privileges?.some(p => p.module === 'expenses' && p.operations.includes('create'));
  const canDelete = user?.isSuperAdmin || user?.privileges?.some(p => p.module === 'expenses' && p.operations.includes('delete'));
  const canUpdate = user?.isSuperAdmin || user?.privileges?.some(p => p.module === 'expenses' && p.operations.includes('update'));
  const canManageCategories = canAdd;
  const canExport = canView;
  const canPermanentDelete = user?.isSuperAdmin;
  const canRestore = canUpdate;

  // ── Initial data (for autocomplete seed) ─────────────────────────────
  const fetchDropdownData = useCallback(async () => {
    try {
      const [usersRes, branchesRes, categoriesRes] = await Promise.all([
        userAPI.getAll({ limit: 50, select: 'name,username' }),
        branchAPI.getAll({ limit: 100 }),
        expenseCategoryAPI.getAll({ limit: 100 }),
      ]);
      setUsers(usersRes.data?.data || []);
      setBranches(branchesRes.data?.data || []);
      setCategories(categoriesRes.data?.data || []);
    } catch {
      toast.error(t('common.fetchMetaError', 'Failed to fetch required data'));
    }
  }, [t]);

  // ── Fetch active expenses ─────────────────────────────────────────────
  const fetchExpenses = useCallback(async () => {
    if (viewMode !== 'active') return;
    setLoading(true);
    setPrivilegeError(false);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (filters.statuses.length > 0) params.statuses = filters.statuses.join(',');
      if (filters.categoryIds.length > 0) params.categoryIds = filters.categoryIds.join(',');
      if (filters.createdByIds.length > 0) params.createdByIds = filters.createdByIds.join(',');
      if (filters.branchIds.length > 0) params.branchIds = filters.branchIds.join(',');
      if (filters.from) params.from = filters.from.toISOString();
      if (filters.to) params.to = filters.to.toISOString();
      if (filters.search) params.search = filters.search;

      const res = await expenseAPI.getAll(params);
      setExpenses(res.data?.data || []);
      setTotalExpenses(res.data?.pagination?.total || 0);
      setTotalAmount(res.data?.totalAmount || 0);
    } catch (err) {
      if (err.response?.status === 403) {
        setPrivilegeError(true);
      } else {
        toast.error(err.response?.data?.message || t('expenses.fetchError'));
      }
      setExpenses([]);
      setTotalExpenses(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, t, viewMode]);

  // ── Fetch deleted expenses ────────────────────────────────────────────
  const fetchDeletedExpenses = useCallback(async () => {
    if (viewMode !== 'deleted') return;
    setLoading(true);
    setPrivilegeError(false);
    try {
      const params = {
        page: deletedPage + 1,
        limit: deletedRowsPerPage,
      };

      if (filters.statuses.length > 0) params.statuses = filters.statuses.join(',');
      if (filters.categoryIds.length > 0) params.categoryIds = filters.categoryIds.join(',');
      if (filters.createdByIds.length > 0) params.createdByIds = filters.createdByIds.join(',');
      if (filters.branchIds.length > 0) params.branchIds = filters.branchIds.join(',');
      if (filters.from) params.from = filters.from.toISOString();
      if (filters.to) params.to = filters.to.toISOString();
      if (filters.search) params.search = filters.search;

      const res = await expenseAPI.getDeleted(params);
      setDeletedExpenses(res.data?.data || []);
      setTotalDeletedExpenses(res.data?.pagination?.total || 0);
      setDeletedTotalAmount(res.data?.totalAmount || 0);
    } catch (err) {
      if (err.response?.status === 403) {
        setPrivilegeError(true);
      } else {
        toast.error(err.response?.data?.message || t('expenses.fetchDeletedError', 'Failed to fetch deleted expenses'));
      }
      setDeletedExpenses([]);
      setTotalDeletedExpenses(0);
    } finally {
      setLoading(false);
    }
  }, [deletedPage, deletedRowsPerPage, filters, t, viewMode]);

  useEffect(() => {
    if (viewMode === 'active') {
      fetchExpenses();
    } else {
      fetchDeletedExpenses();
    }
  }, [fetchExpenses, fetchDeletedExpenses, viewMode]);

  useEffect(() => { fetchDropdownData(); }, [fetchDropdownData]);

  // ── View mode switch ──────────────────────────────────────────────────
  const handleViewModeChange = (_, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      setPage(0);
      setDeletedPage(0);
    }
  };

  // ── Filter helpers ────────────────────────────────────────────────────
  const handleFilterChange = useCallback((key, value) => {
    setPage(0);
    setDeletedPage(0);
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleClearFilters = () => {
    setPage(0);
    setDeletedPage(0);
    setFilters({
      statuses: [], categoryIds: [], createdByIds: [],
      branchIds: [], from: null, to: null, search: '',
    });
  };

  const hasActiveFilters =
    filters.statuses.length > 0 ||
    filters.categoryIds.length > 0 ||
    filters.createdByIds.length > 0 ||
    filters.branchIds.length > 0 ||
    filters.from || filters.to || filters.search;

  // ── Memoized initial options ──────────────────────────────────────────
  const initialCategories = useMemo(() => categories.slice(0, 20), [categories]);
  const initialUsers = useMemo(() => users.slice(0, 20), [users]);
  const initialBranches = useMemo(() => branches.slice(0, 20), [branches]);

  // ── Label helpers ─────────────────────────────────────────────────────
  const getCategoryLabel = useCallback(o => o?.name || '', []);
  const getUserLabel = useCallback(o => o?.name || o?.username || '', []);
  const getBranchLabel = useCallback(o => o?.name || '', []);

  // ── Export ────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (filters.statuses.length > 0) params.statuses = filters.statuses.join(',');
      if (filters.categoryIds.length > 0) params.categoryIds = filters.categoryIds.join(',');
      if (filters.createdByIds.length > 0) params.createdByIds = filters.createdByIds.join(',');
      if (filters.branchIds.length > 0) params.branchIds = filters.branchIds.join(',');
      if (filters.from) params.from = filters.from.toISOString();
      if (filters.to) params.to = filters.to.toISOString();
      if (filters.search) params.search = filters.search;

      const response = await expenseAPI.export(params);
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `expenses_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('expenses.exportSuccess', 'Expenses exported successfully'));
    } catch (err) {
      toast.error(err.response?.data?.message || t('expenses.exportError'));
    } finally {
      setExporting(false);
    }
  };

  // ── Dialog / action helpers ───────────────────────────────────────────
  const handleOpenExpenseDialog = (expense = null) => {
    setSelectedExpense(expense);
    setOpenExpenseDialog(true);
  };
  const handleCloseExpenseDialog = () => {
    setOpenExpenseDialog(false);
    setSelectedExpense(null);
  };

  const triggerConfirm = (title, message, onConfirm) => {
    setConfirmDetails({ title, message, onConfirm });
    setOpenConfirm(true);
  };

  // ── Soft Delete (opens reason dialog) ─────────────────────────────────
  const handleDelete = (expense) => {
    setExpenseToDelete(expense);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async (reason) => {
    if (!expenseToDelete) return;
    try {
      await expenseAPI.delete(expenseToDelete._id, { reason });
      toast.success(t('expenses.deleteSuccess', 'Expense deleted successfully'));
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.message || t('expenses.deleteError', 'Failed to delete expense'));
    } finally {
      setOpenDeleteDialog(false);
      setExpenseToDelete(null);
    }
  };

  // ── Restore ───────────────────────────────────────────────────────────
  const handleRestore = (expense) => triggerConfirm(
    t('expenses.restoreConfirmTitle', 'Restore Expense?'),
    t('expenses.restoreConfirmMessage', 'This expense will be restored to its previous status. Are you sure?'),
    async () => {
      try {
        await expenseAPI.restore(expense._id);
        toast.success(t('expenses.restoreSuccess', 'Expense restored successfully'));
        fetchDeletedExpenses();
      } catch (err) {
        toast.error(err.response?.data?.message || t('expenses.restoreError', 'Failed to restore expense'));
      }
    }
  );

  // ── Permanent Delete ──────────────────────────────────────────────────
  const handlePermanentDelete = (expense) => triggerConfirm(
    t('expenses.permanentDeleteTitle', 'Permanently Delete Expense?'),
    t('expenses.permanentDeleteMessage', 'This action cannot be undone. The expense will be permanently removed from the system.'),
    async () => {
      try {
        await expenseAPI.permanentDelete(expense._id);
        toast.success(t('expenses.permanentDeleteSuccess', 'Expense permanently deleted'));
        fetchDeletedExpenses();
      } catch (err) {
        toast.error(err.response?.data?.message || t('expenses.permanentDeleteError', 'Failed to permanently delete expense'));
      }
    }
  );

  // ── Status Actions ────────────────────────────────────────────────────
  const handleApprove = (expense) => triggerConfirm(
    t('expenses.approveConfirmTitle'),
    t('expenses.approveConfirmMessage'),
    async () => {
      await expenseAPI.approve(expense._id);
      toast.success(t('expenses.approveSuccess'));
      fetchExpenses();
    }
  );

  const handleReject = (expense) => triggerConfirm(
    t('expenses.rejectConfirmTitle'),
    t('expenses.rejectConfirmMessage'),
    async () => {
      await expenseAPI.reject(expense._id);
      toast.success(t('expenses.rejectSuccess'));
      fetchExpenses();
    }
  );

  const handleMarkAsPaid = (expense) => triggerConfirm(
    t('expenses.markAsPaidConfirmTitle'),
    t('expenses.markAsPaidConfirmMessage'),
    async () => {
      await expenseAPI.markAsPaid(expense._id);
      toast.success(t('expenses.markAsPaidSuccess'));
      fetchExpenses();
    }
  );

  // ── Pagination ────────────────────────────────────────────────────────
  const handlePageChange = (_, newPage) => {
    if (viewMode === 'active') setPage(newPage);
    else setDeletedPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (viewMode === 'active') {
      setRowsPerPage(value);
      setPage(0);
    } else {
      setDeletedRowsPerPage(value);
      setDeletedPage(0);
    }
  };

  // ── Status chip colors ────────────────────────────────────────────────
  const statusColor = (s) => ({
    approved: 'success', rejected: 'error',
    paid: 'info', pending: 'warning'
  }[s] || 'default');

  // ── Computed values for current view ──────────────────────────────────
  const currentExpenses = viewMode === 'active' ? expenses : deletedExpenses;
  const currentTotal = viewMode === 'active' ? totalExpenses : totalDeletedExpenses;
  const currentTotalAmount = viewMode === 'active' ? totalAmount : deletedTotalAmount;
  const currentPage = viewMode === 'active' ? page : deletedPage;
  const currentRowsPerPage = viewMode === 'active' ? rowsPerPage : deletedRowsPerPage;

  if (!canView || privilegeError) {
    return <NoPrivilege message={t('expenses.noPrivilegeError')} />;
  }

  return (
    <Box sx={{ p: 3 }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" component="h1">
            {t('expenses.title', 'Expenses')}
          </Typography>
          {!loading && (
            <Chip
              label={`${currentTotal} ${t('common.total', 'total')}`}
              color={viewMode === 'active' ? 'primary' : 'error'}
              variant="outlined"
              size="small"
            />
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          {canExport && viewMode === 'active' && (
            <Button
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
              onClick={handleExport}
              disabled={exporting || loading}
            >
              {exporting ? t('common.exporting', 'Exporting...') : t('common.export', 'Export')}
            </Button>
          )}
          {canManageCategories && (
            <Button variant="outlined" startIcon={<CategoryIcon />}
              onClick={() => setOpenCategoryDialog(true)}>
              {t('expenses.manageCategories', 'Categories')}
            </Button>
          )}
          {canAdd && viewMode === 'active' && (
            <Button variant="contained" startIcon={<Add />}
              onClick={() => handleOpenExpenseDialog()}>
              {t('expenses.add', 'Add Expense')}
            </Button>
          )}
        </Stack>
      </Box>

      {/* ── View Mode Toggle ── */}
      <Box sx={{ mb: 3 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
        >
          <ToggleButton value="active">
            <ActiveIcon sx={{ mr: 1 }} />
            {t('expenses.activeExpenses', 'Active Expenses')}
            {totalExpenses > 0 && (
              <Chip label={totalExpenses} size="small" color="primary" sx={{ ml: 1 }} />
            )}
          </ToggleButton>
          <ToggleButton value="deleted" color="error">
            <TrashIcon sx={{ mr: 1 }} />
            {t('expenses.deletedExpenses', 'Deleted Expenses')}
            {totalDeletedExpenses > 0 && (
              <Chip label={totalDeletedExpenses} size="small" color="error" sx={{ ml: 1 }} />
            )}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* ── Total Amount Card ── */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" color="text.secondary">
          {viewMode === 'active'
            ? t('expenses.totalAmount', 'Total Amount')
            : t('expenses.deletedTotalAmount', 'Deleted Total Amount')
          }
        </Typography>
        <Typography
          variant="h4"
          fontWeight={700}
          color={viewMode === 'active' ? 'primary.main' : 'error.main'}
        >
          {currentTotalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Typography>
      </Paper>

      {/* ── Filters ── */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" gap={2} mb={2}>
          <FilterList />
          <Typography variant="h6">{t('filters', 'Filters')}</Typography>
          {hasActiveFilters && (
            <Chip
              label={t('common.filtersApplied', 'Filters applied')}
              size="small" color="info" variant="outlined"
            />
          )}
        </Stack>

        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">

          {/* Status */}
          <MultiSelectSearchAutocomplete
            label={t('expenses.status', 'Status')}
            searchFunction={null}
            value={filters.statuses}
            onChange={(ids) => handleFilterChange('statuses', ids)}
            initialOptions={STATUS_VALUES.map(v => ({ _id: v, name: t(`appointments.${v}`, v) }))}
            allItems={STATUS_VALUES.map(v => ({ _id: v, name: t(`appointments.${v}`, v) }))}
            getOptionLabel={o => o?.name || o?._id || ''}
            minWidth={180}
          />

          {/* Category */}
          <MultiSelectSearchAutocomplete
            label={t('common.category', 'Category')}
            searchFunction={searchExpenseCategories}
            value={filters.categoryIds}
            onChange={(ids) => handleFilterChange('categoryIds', ids)}
            initialOptions={initialCategories}
            allItems={categories}
            getOptionLabel={getCategoryLabel}
            minWidth={180}
          />

          {/* Created By (User) */}
          <MultiSelectSearchAutocomplete
            label={t('common.user', 'User')}
            searchFunction={searchUsers}
            value={filters.createdByIds}
            onChange={(ids) => handleFilterChange('createdByIds', ids)}
            initialOptions={initialUsers}
            allItems={users}
            getOptionLabel={getUserLabel}
            minWidth={180}
          />

          {/* Branch — superAdmin only */}
          {user?.isSuperAdmin && (
            <MultiSelectSearchAutocomplete
              label={t('common.branch', 'Branch')}
              searchFunction={searchBranches}
              value={filters.branchIds}
              onChange={(ids) => handleFilterChange('branchIds', ids)}
              initialOptions={initialBranches}
              allItems={branches}
              getOptionLabel={getBranchLabel}
              minWidth={180}
            />
          )}

          {/* Date Range */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t('common.from', 'From')}
              value={filters.from}
              onChange={date => handleFilterChange('from', date)}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
            <DatePicker
              label={t('common.to', 'To')}
              value={filters.to}
              onChange={date => handleFilterChange('to', date)}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }}
            />
          </LocalizationProvider>

          {/* Search */}
          <TextField
            label={t('common.search', 'Search')}
            size="small"
            value={filters.search}
            onChange={e => handleFilterChange('search', e.target.value)}
            sx={{ minWidth: 180 }}
          />

          {hasActiveFilters && (
            <Button variant="text" color="inherit"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />} size="small">
              {t('common.clearFilters', 'Clear')}
            </Button>
          )}
        </Box>
      </Paper>

      {/* ── Active Filter Chips ── */}
      {hasActiveFilters && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {filters.statuses.map(s => (
            <Chip
              key={s}
              label={`${t('expenses.status', 'Status')}: ${t(`appointments.${s}`, s)}`}
              size="small"
              color={statusColor(s)}
              onDelete={() => handleFilterChange('statuses', filters.statuses.filter(x => x !== s))}
            />
          ))}
          {filters.categoryIds.length > 0 && (
            <Chip
              label={`${t('common.category', 'Category')}: ${filters.categoryIds.length}`}
              size="small"
              onDelete={() => handleFilterChange('categoryIds', [])}
            />
          )}
          {filters.createdByIds.length > 0 && (
            <Chip
              label={`${t('common.user', 'User')}: ${filters.createdByIds.length}`}
              size="small"
              onDelete={() => handleFilterChange('createdByIds', [])}
            />
          )}
          {filters.branchIds.length > 0 && (
            <Chip
              label={`${t('common.branch', 'Branch')}: ${filters.branchIds.length}`}
              size="small"
              onDelete={() => handleFilterChange('branchIds', [])}
            />
          )}
          {filters.from && (
            <Chip
              label={`${t('common.from', 'From')}: ${filters.from.toLocaleDateString()}`}
              size="small"
              onDelete={() => handleFilterChange('from', null)}
            />
          )}
          {filters.to && (
            <Chip
              label={`${t('common.to', 'To')}: ${filters.to.toLocaleDateString()}`}
              size="small"
              onDelete={() => handleFilterChange('to', null)}
            />
          )}
          {filters.search && (
            <Chip
              label={`${t('common.search', 'Search')}: ${filters.search}`}
              size="small"
              onDelete={() => handleFilterChange('search', '')}
            />
          )}
        </Box>
      )}

      {/* ── Results Info ── */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('common.showing', 'Showing')}{' '}
        {currentTotal === 0 ? 0 : Math.min(currentPage * currentRowsPerPage + 1, currentTotal)}–
        {Math.min((currentPage + 1) * currentRowsPerPage, currentTotal)}{' '}
        {t('common.of', 'of')} {currentTotal} {t('common.items', 'items')}
      </Typography>

      {/* ── Table / Empty ── */}
      {currentExpenses.length === 0 && !loading ? (
        <Paper sx={{ p: 4 }}>
          <NoContent
            icon={viewMode === 'active'
              ? <ReceiptIcon sx={{ fontSize: 64 }} />
              : <TrashIcon sx={{ fontSize: 64, color: 'error.main' }} />
            }
            title={
              viewMode === 'deleted'
                ? t('expenses.noDeletedExpenses', 'No Deleted Expenses')
                : hasActiveFilters
                  ? t('expenses.noFilterResults', 'No expenses match your filters')
                  : t('expenses.noExpensesTitle', 'No Expenses Found')
            }
            description={
              viewMode === 'deleted'
                ? t('expenses.noDeletedDescription', 'Deleted expenses will appear here.')
                : hasActiveFilters
                  ? t('expenses.tryAdjustingFilters', 'Try adjusting your filters')
                  : t('expenses.noExpensesDescription', 'Start by adding your first expense.')
            }
            action={canAdd && viewMode === 'active' && !hasActiveFilters && (
              <Button variant="contained" startIcon={<Add />}
                onClick={() => handleOpenExpenseDialog()}>
                {t('expenses.add', 'Add Expense')}
              </Button>
            )}
          />
        </Paper>
      ) : viewMode === 'active' ? (
        <ExpensesTable
          expenses={expenses}
          loading={loading}
          user={user}
          onEdit={handleOpenExpenseDialog}
          onDelete={handleDelete}
          onApprove={handleApprove}
          onReject={handleReject}
          onMarkAsPaid={handleMarkAsPaid}
          page={page}
          rowsPerPage={rowsPerPage}
          total={totalExpenses}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          filters={filters}
        />
      ) : (
        <DeletedExpensesTable
          expenses={deletedExpenses}
          loading={loading}
          user={user}
          onRestore={handleRestore}
          onPermanentDelete={handlePermanentDelete}
          canRestore={canRestore}
          canPermanentDelete={canPermanentDelete}
          page={deletedPage}
          rowsPerPage={deletedRowsPerPage}
          total={totalDeletedExpenses}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}

      {/* ── Dialogs ── */}
      <AddExpenseDialog
        open={openExpenseDialog}
        onClose={handleCloseExpenseDialog}
        selectedExpense={selectedExpense}
        onSuccess={fetchExpenses}
        categories={categories}
        branches={branches}
        user={user}
      />
      <ManageCategoriesDialog
        open={openCategoryDialog}
        onClose={() => setOpenCategoryDialog(false)}
        categories={categories}
        onRefresh={fetchDropdownData}
      />
      <DeleteExpenseDialog
        open={openDeleteDialog}
        onClose={() => { setOpenDeleteDialog(false); setExpenseToDelete(null); }}
        expense={expenseToDelete}
        onConfirm={handleConfirmDelete}
      />
      <ConfirmDialog
        open={openConfirm}
        title={confirmDetails.title}
        message={confirmDetails.message}
        onConfirm={async () => {
          if (confirmDetails.onConfirm) await confirmDetails.onConfirm();
          setOpenConfirm(false);
        }}
        onClose={() => setOpenConfirm(false)}
      />
    </Box>
  );
};

export default Expenses;